import {
  SchemaDomain,
  SchemaType,
  SchemaVariant,
  type CreateSchemaInput,
  type SchemaRegistry,
} from '@morph-mapper/types';

export type SchemaMetadataValues = Pick<
  CreateSchemaInput,
  'description' | 'domain' | 'name' | 'operatorId' | 'type' | 'variant'
>;

export type SchemaMetadataFormProps = {
  formId: string;
  initialValues?: Partial<SchemaMetadataValues>;
  onSubmit: (payload: SchemaMetadataValues) => Promise<void>;
  readOnly?: boolean;
  schemaRegistry: SchemaRegistry;
};

export type SchemaMetadataDraft = {
  description: string;
  domain: string;
  name: string;
  operatorId: string[];
  type: string;
  variant: string;
};

const isSchemaVariant = (value: string): value is SchemaVariant => {
  return Object.values(SchemaVariant).some((variant) => variant === value);
};

export const toInitialMetadataDraft = (
  initialValues?: Partial<SchemaMetadataValues>,
): SchemaMetadataDraft => ({
  description: initialValues?.description ?? '',
  domain: initialValues?.domain ?? '',
  name: initialValues?.name ?? '',
  operatorId: initialValues?.operatorId ?? [],
  type: initialValues?.type ?? 'standard',
  variant: initialValues?.variant ?? SchemaVariant.Xml,
});

export const normalizeSchemaMetadataValues = (
  draft: SchemaMetadataDraft,
): SchemaMetadataValues => ({
  description: draft.description.trim(),
  domain: draft.domain.trim(),
  name: draft.name.trim(),
  operatorId: Array.from(
    new Set(draft.operatorId.map((value) => value.trim()).filter(Boolean)),
  ),
  type: draft.type,
  variant: draft.variant,
});

const getInvalidSchemaRegistryFields = (
  draft: SchemaMetadataDraft,
  schemaRegistry: SchemaRegistry,
): string[] => {
  const invalidFields: string[] = [];

  const domain = draft.domain as SchemaDomain;
  const type = draft.type as SchemaType;
  const variant = draft.variant as SchemaVariant;

  if (draft.domain.trim() && !schemaRegistry.schemaDomains.includes(domain)) {
    invalidFields.push('domain');
  }

  if (draft.type.trim() && !schemaRegistry.schemaTypes.includes(type)) {
    invalidFields.push('type');
  }

  if (
    draft.variant.trim() &&
    (!isSchemaVariant(variant) ||
      !schemaRegistry.schemaVariants.includes(variant))
  ) {
    invalidFields.push('variant');
  }

  const rules = schemaRegistry.schemaRules?.[domain];

  if (
    !invalidFields.includes('type') &&
    rules?.allowedTypes?.length &&
    !rules.allowedTypes.includes(type)
  ) {
    invalidFields.push('type');
  }

  if (
    !invalidFields.includes('variant') &&
    rules?.allowedVariants?.length &&
    !rules.allowedVariants.includes(variant)
  ) {
    invalidFields.push('variant');
  }

  return invalidFields;
};

export const getInvalidSchemaRegistryMessage = (
  draft: SchemaMetadataDraft,
  schemaRegistry: SchemaRegistry,
): string | null => {
  const invalidFields = getInvalidSchemaRegistryFields(draft, schemaRegistry);

  if (invalidFields.length === 0) {
    return null;
  }

  return `This schema contains invalid registry values for: ${invalidFields.join(
    ', ',
  )}. Update them before saving.`;
};
